/**
 * Cancellation — cooperative cancel for long-running pipelines.
 *
 * The orchestrator and batch generator check the token between stages and
 * chunks; providers receive it so a future adapter can abort an in-flight
 * HTTP request. Cancellation is a state, not an exception — callers decide
 * whether to throw via throwIfCancelled().
 */

export class CancelledError extends Error {
  constructor(message = 'Operation cancelled') {
    super(message);
    this.name = 'CancelledError';
  }
}

export class CancellationToken {
  constructor() {
    this._cancelled = false;
    this._reason = null;
    this._listeners = [];
  }

  get isCancelled() {
    return this._cancelled;
  }

  get reason() {
    return this._reason;
  }

  cancel(reason = 'cancelled by caller') {
    if (this._cancelled) return;
    this._cancelled = true;
    this._reason = reason;
    this._listeners.forEach((fn) => {
      try { fn(reason); } catch { /* listener errors never block cancel */ }
    });
    this._listeners = [];
  }

  /** Register a cleanup hook; fires immediately if already cancelled. */
  onCancel(fn) {
    if (this._cancelled) fn(this._reason);
    else this._listeners.push(fn);
    return this;
  }

  throwIfCancelled() {
    if (this._cancelled) throw new CancelledError(this._reason || undefined);
  }
}
